// Binary wire format for the browser P2P mesh. Mirrors the Rust
// LAN protocol (proto/frames.ts) where it makes sense:
//
//   0x02 PING  joiner→host  u64 client_send_us                       (9 B)
//   0x03 PONG  host→joiner   u64 client_send_us, u64 host_recv_us,
//                            u64 host_send_us                       (25 B)
//   0x10 AUDIO host→joiner   u64 play_at_us, u32 seq, PCM body      (13 + body)
//
// The AUDIO body in the LAN protocol carries Opus bytes; here it
// carries interleaved-stereo f32 LE samples. The Scheduler doesn't
// care which — it ingests Float32Array. We skip Opus encoding on the
// host because the audio is going over the local network and the
// 384 KB/s per peer is cheap; we can revisit if we ever want to push
// streams across the open internet.

export const FRAME_PING = 0x02
export const FRAME_PONG = 0x03
export const FRAME_AUDIO_PCM = 0x10

const HEADER_PING = 1 + 8
const HEADER_PONG = 1 + 24
const HEADER_AUDIO = 1 + 8 + 4

export function encodePing(clientSendUs: bigint): ArrayBuffer {
  const buf = new ArrayBuffer(HEADER_PING)
  const v = new DataView(buf)
  v.setUint8(0, FRAME_PING)
  v.setBigUint64(1, clientSendUs, false)
  return buf
}

export function encodePong(
  clientSendUs: bigint,
  hostRecvUs: bigint,
  hostSendUs: bigint,
): ArrayBuffer {
  const buf = new ArrayBuffer(HEADER_PONG)
  const v = new DataView(buf)
  v.setUint8(0, FRAME_PONG)
  v.setBigUint64(1, clientSendUs, false)
  v.setBigUint64(9, hostRecvUs, false)
  v.setBigUint64(17, hostSendUs, false)
  return buf
}

/** Encode an interleaved-stereo f32 audio frame with timestamp + seq. */
export function encodeAudioPcm(
  playAtUs: bigint,
  seq: number,
  samples: Float32Array,
): ArrayBuffer {
  const body = samples.byteLength
  const buf = new ArrayBuffer(HEADER_AUDIO + body)
  const v = new DataView(buf)
  v.setUint8(0, FRAME_AUDIO_PCM)
  v.setBigUint64(1, playAtUs, false)
  v.setUint32(9, seq, false)
  new Uint8Array(buf, HEADER_AUDIO).set(new Uint8Array(samples.buffer, samples.byteOffset, body))
  return buf
}

export type DecodedFrame =
  | { kind: 'ping'; clientSendUs: bigint }
  | { kind: 'pong'; clientSendUs: bigint; hostRecvUs: bigint; hostSendUs: bigint }
  | { kind: 'audio'; playAtUs: bigint; seq: number; samples: Float32Array }
  | { kind: 'unknown'; type: number }

export function decode(buffer: ArrayBuffer): DecodedFrame {
  if (buffer.byteLength === 0) return { kind: 'unknown', type: -1 }
  const v = new DataView(buffer)
  const type = v.getUint8(0)
  switch (type) {
    case FRAME_PING:
      if (buffer.byteLength < HEADER_PING) return { kind: 'unknown', type }
      return { kind: 'ping', clientSendUs: v.getBigUint64(1, false) }
    case FRAME_PONG:
      if (buffer.byteLength < HEADER_PONG) return { kind: 'unknown', type }
      return {
        kind: 'pong',
        clientSendUs: v.getBigUint64(1, false),
        hostRecvUs: v.getBigUint64(9, false),
        hostSendUs: v.getBigUint64(17, false),
      }
    case FRAME_AUDIO_PCM: {
      if (buffer.byteLength < HEADER_AUDIO) return { kind: 'unknown', type }
      const playAtUs = v.getBigUint64(1, false)
      const seq = v.getUint32(9, false)
      // The PCM body must be 4-byte-aligned to view as Float32 directly.
      // ArrayBuffers from PeerJS aren't always aligned, so copy into a
      // fresh aligned Float32Array.
      const bodyLen = buffer.byteLength - HEADER_AUDIO
      const samples = new Float32Array(bodyLen / 4)
      const view = new DataView(buffer, HEADER_AUDIO)
      for (let i = 0; i < samples.length; i++) {
        samples[i] = view.getFloat32(i * 4, true)
      }
      return { kind: 'audio', playAtUs, seq, samples }
    }
    default:
      return { kind: 'unknown', type }
  }
}
